import {
	type CollectionModelField,
	CollectionModelFieldNestedList,
	CollectionModelFieldSelectList,
	FormModelFieldNestedList,
	FormModelFieldSelectList,
	FormModelFieldSubForm,
	type FormModelField,
} from '@ikegps/core-shared';
import type { MapFieldsState } from './collection.form.migrate.map.fields.state';

export interface ReviewSummary {
	collectionsImpacted: number;
	fieldsMapped: number;
	fieldsLost: FormModelField[];
	/** null while the actual per-collection dropdown values are still being checked against the target form. */
	dropdownValuesLost: number | null;
}

export type ListField = FormModelFieldSelectList | FormModelFieldNestedList;

export interface CheckableListFieldPair {
	sourceField: ListField;
	targetField: ListField;
}

/** Reviewable fields are leaf fields only — groups are containers with no data of their own to lose. */
export function getReviewableFields(state: MapFieldsState): FormModelField[] {
	return state.sourceForm.getFieldsRecursive.filter((field) => !FormModelFieldSubForm.isSubForm(field));
}

/** Mapped source select-list/nested-list fields paired with their same-type target field — the only
 *  fields whose actual collection values are worth checking against the target's option set. */
export function getCheckableListFieldPairs(state: MapFieldsState): CheckableListFieldPair[] {
	const pairs: CheckableListFieldPair[] = [];

	for (const sourceField of getReviewableFields(state)) {
		const targetField = state.getTargetField(sourceField);

		if (targetField == null) {
			continue;
		}

		if (FormModelFieldSelectList.isSelectList(sourceField) && FormModelFieldSelectList.isSelectList(targetField)) {
			pairs.push({ sourceField, targetField });
		} else if (
			FormModelFieldNestedList.isNestedList(sourceField) &&
			FormModelFieldNestedList.isNestedList(targetField)
		) {
			pairs.push({ sourceField, targetField });
		}
	}

	return pairs;
}

/** Whether a collection's actual value for a select-list/nested-list field has no match among the
 *  target field's options — i.e. it will be dropped on migration. Fields with no value don't count. */
export function isListValueLost(collectionField: CollectionModelField, targetField: ListField): boolean {
	if (
		CollectionModelFieldSelectList.isSelectListField(collectionField) &&
		FormModelFieldSelectList.isSelectList(targetField)
	) {
		return collectionField.hasValue() && targetField.findOption(collectionField.value) == null;
	}

	if (
		CollectionModelFieldNestedList.isNestedListField(collectionField) &&
		FormModelFieldNestedList.isNestedList(targetField)
	) {
		const value = collectionField.value;
		return collectionField.hasValue() && targetField.findOptions(value).length !== value.length;
	}

	return false;
}
